import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { skills, Skill } from '../data/skills'
import { SkillCard } from '../components/SkillCard'
import {
  Container,
  Section,
  Title,
  Subtitle,
  Button,
  Grid,
} from '../styles/common.styles'
import styled from 'styled-components'

const SkillsContainer = styled(Container)`
  min-height: 100vh;
  background-color: #0a0a0a;
  color: #ffffff;
  padding-top: 8rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding-top: 6rem;
  }
`

const CategoryFilter = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 3rem;
`

const CategoryButton = styled.button<{ $active: boolean }>`
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  border: 2px solid
    ${(props) => (props.$active ? '#4ecdc4' : 'rgba(255, 255, 255, 0.1)')};
  background: ${(props) =>
    props.$active ? 'rgba(78, 205, 196, 0.1)' : 'transparent'};
  color: ${(props) => (props.$active ? '#4ecdc4' : '#ffffff')};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? 'rgba(78, 205, 196, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
    transform: translateY(-2px);
  }
`

const SkillsGrid = styled(Grid)`
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const categories: Skill['category'][] = [
  'Frontend',
  'Backend',
  'Database',
  'DevOps',
  'Tools',
  'Languages',
]

export function SkillsPage() {
  const [selectedCategory, setSelectedCategory] = useState<
    Skill['category'] | 'All'
  >('All')

  const filteredSkills = useMemo(() => {
    if (selectedCategory === 'All') {
      return skills.sort((a, b) => (a.featured ? -1 : 1))
    }
    return skills
      .filter((skill) => skill.category === selectedCategory)
      .sort((a, b) => (a.featured ? -1 : 1))
  }, [selectedCategory])

  return (
    <SkillsContainer>
      <Section>
        <Title>Skills & Expertise</Title>
        <Subtitle>
          A comprehensive overview of my technical skills and years of
          experience
        </Subtitle>

        <CategoryFilter>
          <CategoryButton
            $active={selectedCategory === 'All'}
            onClick={() => setSelectedCategory('All')}
            role='button'
            aria-pressed={selectedCategory === 'All'}>
            All
          </CategoryButton>
          {categories.map((category) => (
            <CategoryButton
              key={category}
              $active={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
              role='button'
              aria-pressed={selectedCategory === category}>
              {category}
            </CategoryButton>
          ))}
        </CategoryFilter>

        <SkillsGrid>
          {filteredSkills.map((skill) => (
            <SkillCard key={skill.id} skill={skill} />
          ))}
        </SkillsGrid>
      </Section>
    </SkillsContainer>
  )
}
